/**
 * @file 文件相关常量
 */

/**
* 状态
* init: 初始状态
* progress: 导入中
* success: 成功
* error: 失败
*/
export enum FileStatus {
    Init = 'init',
    Progress = 'progress',
    Success = 'success',
    Error = 'error',
}

/**
 * 文件类型
 */
export enum FileType {
    PPT = 'ppt',
    Excel = 'excel', // 导入为在线表格
    Word = 'word',
    PDF = 'pdf',
    ExcelDst = 'exceldst', // 导入为数据表
}

/**
 * 导入文件大小限制
 */
export const UPLOAD_MAXSZIE = {
    [FileType.PPT]: 0.5 * 2 ** 30, // 500M
    [FileType.Excel]: 0.1 * 2 ** 30, // 100M
    [FileType.Word]: 0.05 * 2 ** 30, // 50M
    [FileType.PDF]: 0.5 * 2 ** 30, // 500M
    [FileType.ExcelDst]: 0.02 * 2 ** 30, // 20M
};

/**
 * 展示文案映射
 */
export const FILE_MAX_TIP_MAP = {
    [FileType.PPT]: '不支持导入大于500M的PPT',
    [FileType.Excel]: '不支持导入大于100M的Excel',
    [FileType.Word]: '不支持导入大于50M的Word',
    [FileType.PDF]: '不支持导入大于500M的PDF',
    [FileType.ExcelDst]: '不支持导入大于20M的Excel',
};

/**
 * 文件类型映射
 */
export const FILE_TYPE_MAP = {
    [FileType.PPT]: ['ppt', 'pptx', 'pptm', 'ppsx', 'ppsm', 'pps', 'potx', 'potm', 'dpt', 'dps', 'pot'],
    [FileType.Excel]: ['xlsx', 'xls', 'csv'],
    [FileType.Word]: ['doc', 'docx', 'docm'],
    [FileType.PDF]: ['pdf'],
    [FileType.ExcelDst]: ['xlsx', 'xls', 'csv'],
};

/**
 * 导入文件支持类型映射(input 标签的 accept 属性使用)
 */
export const UPLOAD_FILE_ACCEPT_MAP = (() => {
    const obj: {
        [key in FileType]?: string
    } = {};

    // 转换为 input 的 accept 接收的格式，如 Word 是 ‘.doc,.docx’
    Object.keys(FILE_TYPE_MAP).forEach(type => {
        obj[type as FileType] = FILE_TYPE_MAP[type as FileType].map((suffix: string) => `.${suffix}`).join(',');
    });
    return obj;
})();

/**
 * 表格导入错误文案映射
 */
export const EXCEL_UPLOAD_ERROR_MAP = {
    120502: '表格有密码保护，请取消密码后导入',
    120503: '表格解析后的数据大于 80M，请拆分之后再导入',
    120505: '表格内容过多，或存在大量空白行列，请尝试删除后再导入',
    120506: '不支持导入Strict Open XML 格式，请另存为（*.xlsx）再导入',
    120508: '不支持导入XLSB格式，请另存为（*.xlsx）再导入',
    120509: '表格单元格数量已超过 200 万个，请尝试将表格拆分之后再导入',
    120510: '表格单元格总数量已超过 500 万个，请尝试将表格拆分之后再导入',
    120512: '导入文件过大，请请尝试将文件拆分之后再导入',
    120513: '表格内容过多，或存在大量空白行列，请尝试删除后再导入',
};

/**
 * 维格默认导入错误码
 */
export const EXCEL_DST_DEFAULT_ERROR_CODE = 240;

/**
 * 数据表导入错误文案映射
 * PS：维格这里很坑，返回的错误码都是 204，所以还需要结合返回的文案去做映射
 */
export const EXCEL_DST_UPLOAD_ERROR_MAP = {
    'file format error': '不支持导入该格式，请另存为.xlsx或.csv后再导入',
    '文件格式错误': '不支持导入该格式，请另存为.xlsx或.csv后再导入',
    'file requires permission password to open': '表格有密码保护，请取消密码后导入',
    '文件需要权限密码才能打开': '表格有密码保护，请取消密码后导入',
    'a single upload file should not exceed 20 mb': '表格大于 20M，请拆分之后再导入',
    '单次上传文件不要超过20MB哟~': '表格大于 20M，请拆分之后再导入',
    'The number of lines exceeds the limit of 50000 lines': '表格数据已超过5万行，请尝试将表格拆分之后再导入',
    '行数超过50000行限制': '表格数据已超过5万行，请尝试将表格拆分之后再导入',
    'the number of columns exceeds the 200 limit': '表格数据已超过200列，请尝试将表格拆分之后再导入',
    '列数超过200列限制': '表格数据已超过200列，请尝试将表格拆分之后再导入',
};
